import type { CategoryDetailResponse, PortalCategoryResponse } from "../../types/tickets";
import type { ArticleCollectionResponse } from "../../types/articleCollections";

/**
 * One row of a tree picker: a ticket category or a knowledge base collection, in tree order (each parent followed by
 * its children). `path` is what search matches and what the closed picker shows ("Billing › Refunds").
 */
export interface TreeOption {
  id: string;
  name: string;
  path: string;
  // 1 for a top-level row
  depth: number;
  parentId?: string | null;
  archived?: boolean;
  // Shown but not selectable (e.g. too deep to be a parent)
  disabled?: boolean;
  // A short note after the name ("Hidden from customers")
  hint?: string;
}

export function categoryToOption(category: CategoryDetailResponse): TreeOption {
  return {
    id: category.id,
    name: category.name,
    path: category.path,
    depth: category.depth,
    parentId: category.parentCategoryId,
    archived: !!category.archivedAt,
  };
}

export function portalCategoryToOption(category: PortalCategoryResponse): TreeOption {
  return {
    id: category.id,
    name: category.name,
    path: category.path,
    depth: category.depth,
    parentId: category.parentCategoryId,
  };
}

export function collectionToOption(collection: ArticleCollectionResponse): TreeOption {
  return {
    id: collection.id,
    name: collection.name,
    path: collection.path,
    depth: collection.depth,
    parentId: collection.parentCollectionId,
  };
}

/**
 * Options for picking a category for new work: active ones only, plus the current value when it's archived, so an
 * edit form still shows (and can keep) what the record already has.
 */
export function activeCategoryOptions(
  categories: CategoryDetailResponse[] | undefined,
  currentId?: string | null,
  currentFallback?: { id: string; name: string; path?: string } | null
): TreeOption[] {
  const all = categories ?? [];
  const options = all.filter((c) => !c.archivedAt).map(categoryToOption);
  if (currentId && !options.some((o) => o.id === currentId)) {
    const archived = all.find((c) => c.id === currentId);
    if (archived) {
      options.push({ ...categoryToOption(archived), depth: 1, archived: true });
    } else if (currentFallback) {
      options.push({
        id: currentFallback.id,
        name: currentFallback.name,
        path: currentFallback.path ?? currentFallback.name,
        depth: 1,
        archived: true,
      });
    }
  }
  return options;
}

/** Case-insensitive match on the full path, so "refund" finds "Billing › Refunds" */
export function matchesQuery(option: TreeOption, query: string): boolean {
  const q = query.trim().toLowerCase();
  return !q || option.path.toLowerCase().includes(q);
}
